import { EventDetails, TrackingEvents, TrackingStage } from '@/config/tracking';
import { GET_SCHOOL } from '@/graphql/queries';
import { districtVar } from '@/graphql/state';
import { AllFlyerFilters, GetSchoolResponse, OwningTypeEnum } from '@/graphql/types';
import {
  useAnalytics,
  useChildGrades,
  useCombinedCategories,
  useSchools,
  useURLSearchParams,
} from '@/hooks';
import { getGradeRange } from '@/utils/getGradeRange';
import { useQuery, useReactiveVar } from '@apollo/client';
import { PropsWithChildren, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import AuthenticationContext from './AuthenticationContext';
import JarFlyerboardContext, { DrawerFilters } from './JarFlyerboardContext';

interface JarFlyerboardProviderType extends PropsWithChildren<{}> {}

export const JAR_TAG_FILTERS = {
  SCHOOL: 'school',
  COMMUNITY: 'community',
  FREE: 'free',
} as const;

export type JarTagFilter = (typeof JAR_TAG_FILTERS)[keyof typeof JAR_TAG_FILTERS];

const JarFlyerboardProvider: React.FC<JarFlyerboardProviderType> = ({ children }) => {
  const isUpdatingFromURL = useRef(false);
  const { grades } = useChildGrades();
  const { audienceId, audienceType } = useURLSearchParams();
  const { subscriptionsIds } = useSchools();
  const [drawerFilters, setDrawerFilters] = useState<DrawerFilters>({ schoolIds: subscriptionsIds });
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useContext(AuthenticationContext);
  const { expandCombinedCategories, compressToCombinedCategories } = useCombinedCategories({});
  const { data: schoolData } = useQuery<GetSchoolResponse>(GET_SCHOOL, {
    variables: {
      schoolId: Number(audienceId),
    },
    skip: searchParams.get('audienceType') !== 'school' || subscriptionsIds.length > 0,
  });

  const district = useReactiveVar(districtVar);
  const districtId = useMemo(() => {
    if (audienceType === 'district') {
      return audienceId;
    }

    if (district?.districtId) {
      return district.districtId;
    }
    if (audienceType === 'school') {
      return schoolData?.getSchool.districtId?.toString();
    }
    return district?.districtId;
  }, [district, audienceId, audienceType, schoolData]);

  const { trackEvent } = useAnalytics();
  const trackingFrom = {
    event_from: TrackingEvents.viewFlyerSet,
    event_detail_from: 'jar',
    stage_from: TrackingStage.discovery,
  };

  useEffect(() => {
    if (isUpdatingFromURL.current) {
      isUpdatingFromURL.current = false;
      return;
    }

    if (!grades || grades.length === 0) {
      return;
    }

    const filterFromParams = searchParams.get('quickFilter') as JarTagFilter | null;
    const fromGrade = searchParams.get('fromGrade');
    const toGrade = searchParams.get('toGrade');
    const typesFromParams = searchParams.get('types');
    const schoolIdsFromParams = searchParams.get('schoolIds');

    // If no meaningful URL parameters exist, don't update filters
    if (!filterFromParams && !fromGrade && !toGrade && !typesFromParams && !schoolIdsFromParams) {
      return;
    }

    const newFilters: DrawerFilters = { schoolIds: subscriptionsIds };

    if (filterFromParams && Object.values(JAR_TAG_FILTERS).includes(filterFromParams)) {
      newFilters.owningType =
        filterFromParams === JAR_TAG_FILTERS.SCHOOL
          ? OwningTypeEnum.district
          : filterFromParams === JAR_TAG_FILTERS.COMMUNITY
          ? OwningTypeEnum.organization
          : undefined;
      newFilters.isCommunityFree = filterFromParams === JAR_TAG_FILTERS.FREE ? true : undefined;
    } else if (!filterFromParams) {
      newFilters.owningType = undefined;
      newFilters.isCommunityFree = undefined;
    }

    if (fromGrade && toGrade && grades.length > 0) {
      const defaultGrades = grades.map(g => g.id);
      const gradeRange = getGradeRange(fromGrade, toGrade).map(g => g.id);
      const gradesAreDifferent =
        gradeRange.length !== defaultGrades.length ||
        !gradeRange.every(grade => defaultGrades.includes(grade));

      if (gradesAreDifferent) {
        newFilters.grades = gradeRange;
      }
    }

    if (typesFromParams) {
      newFilters.categories = typesFromParams.split(',');
    }
    if (schoolIdsFromParams) {
      newFilters.schoolIds = schoolIdsFromParams.split(',').map(id => Number(id));
    }

    if (Object.keys(newFilters).length > 0) {
      setDrawerFilters(prev => ({ ...prev, ...newFilters }));
    }
  }, [searchParams, grades]);

  const filters = useMemo((): AllFlyerFilters | undefined => {
    if (!grades || grades.length === 0) return;

    const gradesToUse = drawerFilters.grades?.length ? drawerFilters.grades : grades.map(g => g.id);

    let schoolIds: number[] = [];

    if (isAuthenticated) {
      schoolIds = drawerFilters.schoolIds;
    } else {
      if (audienceType === 'school' && audienceId && audienceId !== 'all') {
        schoolIds = [Number(audienceId)];
      }
    }

    let base: AllFlyerFilters = {
      allDistrictSchools: !isAuthenticated && schoolIds.length === 0,
      schoolIds,
      includeExpandedCommunity: true,
      grades: gradesToUse,
    };

    if (drawerFilters.owningType) {
      base.owningType = drawerFilters.owningType;
    }

    if (drawerFilters.isCommunityFree) {
      base.isCommunityFree = true;
    }

    if (drawerFilters.categories?.length) {
      const categories = expandCombinedCategories(
        drawerFilters.categories.map(cat => cat.toString())
      );
      base.categories = categories;
    }

    return base;
  }, [
    drawerFilters,
    grades,
    subscriptionsIds,
    isAuthenticated,
    expandCombinedCategories,
    audienceId,
    audienceType,
  ]);

  const toggleQuickFilter = (tag: string) => {
    const filterMapping = {
      [JAR_TAG_FILTERS.SCHOOL]: { owningType: OwningTypeEnum.district },
      [JAR_TAG_FILTERS.COMMUNITY]: { owningType: OwningTypeEnum.organization },
      [JAR_TAG_FILTERS.FREE]: { isCommunityFree: true },
    };

    const isCurrentlyActive =
      (tag === JAR_TAG_FILTERS.SCHOOL && drawerFilters.owningType === OwningTypeEnum.district) ||
      (tag === JAR_TAG_FILTERS.COMMUNITY &&
        drawerFilters.owningType === OwningTypeEnum.organization) ||
      (tag === JAR_TAG_FILTERS.FREE && drawerFilters.isCommunityFree === true);

    let newFilters: DrawerFilters = { schoolIds: drawerFilters.schoolIds };

    if (isCurrentlyActive) {
      if (tag === JAR_TAG_FILTERS.SCHOOL || tag === JAR_TAG_FILTERS.COMMUNITY) {
        newFilters.owningType = undefined;
      } else if (tag === JAR_TAG_FILTERS.FREE) {
        newFilters.isCommunityFree = undefined;
      }
    } else {
      const mapped = filterMapping[tag];
      newFilters = {
        ...mapped,
        ...(tag === JAR_TAG_FILTERS.FREE ? { owningType: undefined } : {}),
        ...(tag !== JAR_TAG_FILTERS.FREE ? { isCommunityFree: undefined } : {}),
      };

      trackEvent({
        event: TrackingEvents.filterFlyerSet,
        event_detail: EventDetails.quickFilter(tag),
        stage: TrackingStage.discovery,
        ...trackingFrom,
      });
    }

    setDrawerFiltersWithURLSync(newFilters);
  };

  const resetQuickFilters = () => {
    setDrawerFiltersWithURLSync({
      owningType: undefined,
      isCommunityFree: undefined,
      grades: undefined,
      categories: undefined,
      schoolIds: [],
    });
  };

  const setDrawerFiltersWithURLSync = (value: DrawerFilters) => {
    isUpdatingFromURL.current = true;
    const newFilters = { ...drawerFilters, ...value };
    const newSearchParams = new URLSearchParams(searchParams);

    if (newFilters.owningType === OwningTypeEnum.district) {
      newSearchParams.set('quickFilter', JAR_TAG_FILTERS.SCHOOL);
    } else if (newFilters.owningType === OwningTypeEnum.organization) {
      newSearchParams.set('quickFilter', JAR_TAG_FILTERS.COMMUNITY);
    } else if (newFilters.isCommunityFree === true) {
      newSearchParams.set('quickFilter', JAR_TAG_FILTERS.FREE);
    } else {
      newSearchParams.delete('quickFilter');
    }

    if (
      newFilters.grades &&
      newFilters.grades.length > 0 &&
      newFilters.grades.length !== grades.length
    ) {
      newSearchParams.set('fromGrade', newFilters.grades[0]);
      newSearchParams.set('toGrade', newFilters.grades[newFilters.grades.length - 1]);
    } else {
      newSearchParams.delete('fromGrade');
      newSearchParams.delete('toGrade');
    }

    if (newFilters.categories && newFilters.categories.length > 0) {
      const categories = compressToCombinedCategories(
        newFilters.categories.map(cat => cat.toString())
      );
      newSearchParams.set('types', categories.join(','));
    } else {
      newSearchParams.delete('types');
    }
    if (newFilters.schoolIds.length > 0 && newFilters.schoolIds.length !== subscriptionsIds.length) {
      newSearchParams.set('schoolIds', newFilters.schoolIds.join(','));
    } else {
      newSearchParams.delete('schoolIds');
    }

    setSearchParams(newSearchParams, { replace: true });
    setDrawerFilters(newFilters);
  };

  const amountActiveFilters = useMemo(() => {
    const hasSchoolFilter =
      drawerFilters.schoolIds.length !== subscriptionsIds.length ||
      !drawerFilters.schoolIds.every(id => subscriptionsIds.includes(id)) ||
      !subscriptionsIds.every(id => drawerFilters.schoolIds.includes(id));

    return (
      Number(drawerFilters.owningType !== undefined) +
      Number(drawerFilters.isCommunityFree === true) +
      Number((drawerFilters.categories?.length ?? 0) > 0) +
      Number(
        (drawerFilters.grades?.length ?? 0) > 0 &&
          (drawerFilters.grades?.length ?? 0) !== grades.length
      ) +
      Number(hasSchoolFilter)
    );
  }, [
    drawerFilters.owningType,
    drawerFilters.isCommunityFree,
    drawerFilters.categories,
    drawerFilters.grades,
    drawerFilters.schoolIds,
    grades.length,
    subscriptionsIds,
  ]);

  const contextState = useMemo(
    () => ({
      filters,
      toggleQuickFilter,
      resetQuickFilters,
      amountActiveFilters,
      drawerFilters,
      setDrawerFilters: setDrawerFiltersWithURLSync,
      districtId,
      trackingFrom,
    }),
    [filters, amountActiveFilters, drawerFilters, districtId, trackingFrom]
  );

  return (
    <JarFlyerboardContext.Provider value={contextState}>{children}</JarFlyerboardContext.Provider>
  );
};

export default JarFlyerboardProvider;
